/**
 * Share modal component - privacy warning and download options with preview
 */

import { LifeStats } from '../utils/calculations';
import { createShareCard, ShareCardOptions } from './ShareCard';
import {
    generateShareImage,
    generateInstagramStoryImage,
    downloadImage,
    showToast,
} from '../utils/shareUtils';

export interface ShareModalOptions {
    stats: LifeStats;
    quote?: {
        text: string;
        author: string;
    };
    onClose: () => void;
}

/**
 * Create the share modal with privacy warning and download options
 */
export function createShareModal(options: ShareModalOptions): HTMLElement {
    const { stats, quote, onClose } = options;

    // Track state
    let isGenerating = false;
    let currentFormat: string | null = null;
    let currentBlob: Blob | null = null;

    // Overlay
    const overlay = document.createElement('div');
    overlay.className = 'share-modal-overlay';
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            closeModal();
        }
    });

    // Modal container
    const modal = document.createElement('div');
    modal.className = 'share-modal';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'share-modal-close';
    closeBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
    `;
    closeBtn.addEventListener('click', closeModal);
    modal.appendChild(closeBtn);

    // Header
    const header = document.createElement('div');
    header.className = 'share-modal-header';
    header.innerHTML = `
        <h2>Download & Share</h2>
        <p class="share-modal-subtitle">Choose a format for your image</p>
    `;
    modal.appendChild(header);

    // Privacy warning
    const warning = document.createElement('div');
    warning.className = 'share-privacy-warning';
    warning.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4m0 4h.01"/>
        </svg>
        <span>This image contains personal information (your age). Share thoughtfully.</span>
    `;
    modal.appendChild(warning);

    // Hidden share card for capturing
    const shareCardOptions: ShareCardOptions = { stats, quote };
    const shareCard = createShareCard(shareCardOptions);
    shareCard.style.position = 'absolute';
    shareCard.style.left = '-9999px';
    document.body.appendChild(shareCard);

    // Preview container (hidden initially, shows generated image)
    const previewSection = document.createElement('div');
    previewSection.className = 'share-preview-section';
    previewSection.style.display = 'none';

    const previewImage = document.createElement('img');
    previewImage.className = 'share-preview-image';
    previewImage.alt = 'Preview';
    previewSection.appendChild(previewImage);

    const previewActions = document.createElement('div');
    previewActions.className = 'share-preview-actions';

    const backBtn = document.createElement('button');
    backBtn.className = 'btn btn-secondary';
    backBtn.textContent = '← Back';
    backBtn.addEventListener('click', () => showFormatSelection());

    const downloadBtn = document.createElement('button');
    downloadBtn.className = 'btn btn-primary';
    downloadBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
        </svg>
        Download
    `;
    downloadBtn.addEventListener('click', handleDownload);

    previewActions.appendChild(backBtn);
    previewActions.appendChild(downloadBtn);
    previewSection.appendChild(previewActions);

    modal.appendChild(previewSection);

    // Format selection section
    const formatSection = document.createElement('div');
    formatSection.className = 'share-format-section';

    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = 'share-buttons';

    // Format options with aspect ratio icons
    const formats = [
        {
            id: 'square',
            label: 'Square',
            subtitle: '1:1',
            icon: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="4" y="4" width="24" height="24" rx="2"/>
            </svg>`,
        },
        {
            id: 'landscape',
            label: 'Landscape',
            subtitle: '16:9',
            icon: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="2" y="8" width="28" height="16" rx="2"/>
            </svg>`,
        },
        {
            id: 'story',
            label: 'Story',
            subtitle: '9:16',
            icon: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="8" y="2" width="16" height="28" rx="2"/>
            </svg>`,
        },
        {
            id: 'full',
            label: 'Full Story',
            subtitle: 'All Stats',
            icon: `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="8" y="1" width="16" height="30" rx="2"/>
                <line x1="11" y1="7" x2="21" y2="7"/>
                <line x1="11" y1="12" x2="21" y2="12"/>
                <line x1="11" y1="17" x2="21" y2="17"/>
                <line x1="11" y1="22" x2="21" y2="22"/>
                <line x1="11" y1="27" x2="17" y2="27"/>
            </svg>`,
        },
    ];

    formats.forEach((format) => {
        const button = document.createElement('button');
        button.className = 'share-btn format-btn';
        button.setAttribute('data-format', format.id);
        button.innerHTML = `
            <div class="format-icon">${format.icon}</div>
            <div class="format-info">
                <span class="format-label">${format.label}</span>
                <span class="format-subtitle">${format.subtitle}</span>
            </div>
        `;
        button.addEventListener('click', () => showPreview(format.id));
        buttonsContainer.appendChild(button);
    });

    formatSection.appendChild(buttonsContainer);
    modal.appendChild(formatSection);
    overlay.appendChild(modal);

    // Show preview for selected format
    async function showPreview(formatId: string) {
        if (isGenerating) return;
        isGenerating = true;
        currentFormat = formatId;

        // Show loading state on button
        const button = buttonsContainer.querySelector(`[data-format="${formatId}"]`) as HTMLButtonElement;
        if (button) button.classList.add('loading');

        // Update header immediately
        const formatInfo = formats.find(f => f.id === formatId);
        header.innerHTML = `
            <h2>${formatInfo?.label || 'Preview'}</h2>
            <p class="share-modal-subtitle">${formatInfo?.subtitle || ''}</p>
        `;

        // Switch views immediately
        formatSection.style.display = 'none';
        previewSection.style.display = 'block';

        // Generate and show placeholder immediately
        const placeholderBlob = generatePlaceholderImage(formatId);
        const placeholderUrl = URL.createObjectURL(placeholderBlob);
        previewImage.src = placeholderUrl;

        // Disable download button while generating
        downloadBtn.disabled = true;
        downloadBtn.innerHTML = `
            <svg class="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16z" opacity="0.25"/>
                <path d="M12 2C6.477 2 2 6.477 2 12" />
            </svg>
            Generating...
        `;

        // Use timeout to allow UI to update before starting heavy work
        setTimeout(async () => {
            try {
                let blob: Blob;

                switch (formatId) {
                    case 'square':
                        blob = await generateShareImage(shareCard);
                        break;
                    case 'landscape':
                        blob = await generateLandscapeImage(shareCard);
                        break;
                    case 'story':
                        blob = await generateInstagramStoryImage(shareCard);
                        break;
                    case 'full':
                        blob = await generateFullStoryImage(stats);
                        break;
                    default:
                        blob = await generateShareImage(shareCard);
                }

                currentBlob = blob;
                const imageUrl = URL.createObjectURL(blob);
                previewImage.onload = () => {
                    URL.revokeObjectURL(placeholderUrl); // Clean up placeholder
                };
                previewImage.src = imageUrl;

                // Enable download button
                downloadBtn.disabled = false;
                downloadBtn.innerHTML = `
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                    </svg>
                    Download
                `;

            } catch (error) {
                console.error('Failed to generate preview:', error);
                showToast('Failed to generate preview');
                // Go back if failed
                showFormatSelection();
            } finally {
                isGenerating = false;
                if (button) button.classList.remove('loading');
            }
        }, 50);
    }

    // Show format selection (back from preview)
    function showFormatSelection() {
        formatSection.style.display = 'block';
        previewSection.style.display = 'none';
        header.innerHTML = `
            <h2>Download & Share</h2>
            <p class="share-modal-subtitle">Choose a format for your image</p>
        `;
        currentFormat = null;
        currentBlob = null;
    }

    // Download current preview
    function handleDownload() {
        if (!currentBlob || !currentFormat) return;

        const filenames: Record<string, string> = {
            square: 'finite-life-square.png',
            landscape: 'finite-life-landscape.png',
            story: 'finite-life-story.png',
            full: 'finite-life-full.png',
        };

        const filename = filenames[currentFormat] || 'finite-life-weeks.png';
        downloadImage(currentBlob, filename);
        showToast(`Downloaded ${filename}`);
    }

    // Close function
    function closeModal() {
        // Clean up hidden share card
        if (shareCard.parentNode) {
            shareCard.parentNode.removeChild(shareCard);
        }
        overlay.classList.add('closing');
        setTimeout(() => {
            overlay.remove();
            onClose();
        }, 200);
    }

    // Handle escape key
    function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            closeModal();
            document.removeEventListener('keydown', handleKeyDown);
        }
    }
    document.addEventListener('keydown', handleKeyDown);

    // Animate in
    requestAnimationFrame(() => {
        overlay.classList.add('visible');
    });

    return overlay;
}

/**
 * Generate a 16:9 landscape image
 */
async function generateLandscapeImage(element: HTMLElement): Promise<Blob> {
    const { default: html2canvas } = await import('html2canvas');

    const landscapeWidth = 1920;
    const landscapeHeight = 1080;

    const canvas = await html2canvas(element, {
        backgroundColor: '#F5F3F0',
        scale: 2,
        useCORS: true,
        logging: false,
    });

    const landscapeCanvas = document.createElement('canvas');
    landscapeCanvas.width = landscapeWidth;
    landscapeCanvas.height = landscapeHeight;
    const ctx = landscapeCanvas.getContext('2d')!;

    ctx.fillStyle = '#F5F3F0';
    ctx.fillRect(0, 0, landscapeWidth, landscapeHeight);

    const contentScale = Math.min(
        (landscapeWidth * 0.85) / canvas.width,
        (landscapeHeight * 0.85) / canvas.height
    );
    const scaledWidth = canvas.width * contentScale;
    const scaledHeight = canvas.height * contentScale;
    const x = (landscapeWidth - scaledWidth) / 2;
    const y = (landscapeHeight - scaledHeight) / 2;

    ctx.drawImage(canvas, x, y, scaledWidth, scaledHeight);

    return new Promise((resolve, reject) => {
        landscapeCanvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to generate landscape image'));
        }, 'image/png', 1.0);
    });
}

/**
 * Generate a full story image with original page components + 3D isometric cube for grid
 */
async function generateFullStoryImage(stats: import('../utils/calculations').LifeStats): Promise<Blob> {
    const { default: html2canvas } = await import('html2canvas');

    const vizContent = document.querySelector('.viz-content') as HTMLElement;
    if (!vizContent) throw new Error('Visualization content not found');

    // Clone the content for manipulation
    const clone = vizContent.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.width = '800px';
    clone.style.background = '#F5F3F0';
    document.body.appendChild(clone);

    try {
        // Hide the footer with share/start over buttons
        const footer = clone.querySelector('.viz-footer') as HTMLElement;
        if (footer) footer.style.display = 'none';

        // Hide data source
        const dataSource = clone.querySelector('.data-source') as HTMLElement;
        if (dataSource) dataSource.style.display = 'none';

        // Calculate grid dimensions to FULLY utilize width
        // We'll calculate the placeholder height based on the grid size
        const cubeSize = 8;
        const cubeGap = 2;
        const contentWidth = 720; // 800px - 40px padding on each side
        const cols = Math.floor((contentWidth + cubeGap) / (cubeSize + cubeGap));
        const rows = Math.ceil(stats.totalWeeks / cols);

        let gridPlaceholderHeight = rows * (cubeSize + cubeGap) + 40; // Grid height + vertical padding

        // Update placeholder height
        const weeksContainer = clone.querySelector('.weeks-container') as HTMLElement;
        if (weeksContainer) {
            weeksContainer.innerHTML = `<div style="height: ${gridPlaceholderHeight}px; background: #F5F3F0;"></div>`;
        }

        // Capture the page with dynamic placeholder
        const pageCanvas = await html2canvas(clone, {
            backgroundColor: '#F5F3F0',
            scale: 2,
            useCORS: true,
            logging: false,
            width: 800,
        });

        // Get position of weeks section for overlay
        const weeksSection = clone.querySelector('.weeks-section') as HTMLElement;
        const wsSectionTop = weeksSection?.offsetTop || 200;

        // Create output canvas
        const padding = 40;
        const brandingHeight = 60;
        const outputWidth = pageCanvas.width + (padding * 2);
        const outputHeight = pageCanvas.height + (padding * 2) + brandingHeight;

        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = outputWidth;
        outputCanvas.height = outputHeight;
        const ctx = outputCanvas.getContext('2d')!;

        // Fill background
        ctx.fillStyle = '#F5F3F0';
        ctx.fillRect(0, 0, outputWidth, outputHeight);

        // Draw captured page
        ctx.drawImage(pageCanvas, padding, padding);

        // Draw cube matrix over the grid placeholder area
        ctx.save();
        ctx.scale(2, 2); // Work in logical pixels relative to valid 800px width

        const totalWeeks = stats.totalWeeks;
        const livedWeeks = stats.weeksLived;

        // Calculate matrix position relative to the captured content area
        const matrixWidth = cols * (cubeSize + cubeGap) - cubeGap;
        const matrixHeight = rows * (cubeSize + cubeGap) - cubeGap;

        // Center within the 800px canvas area, then offset by output padding (40 / 2 = 20 logical)
        // Actually, output padding is 40 physical pixels, so 20 logical pixels.
        // wsSectionTop is logical pixels from the clone.
        // gridPlaceholderHeight is logical pixels.

        const logicalPadding = padding / 2; // 20px
        const contentAreaWidth = 800;

        const startX = logicalPadding + (contentAreaWidth - matrixWidth) / 2;
        const startY = logicalPadding + wsSectionTop + (gridPlaceholderHeight - matrixHeight) / 2;

        let weekIndex = 0;
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                if (weekIndex >= totalWeeks) continue;
                const isLived = weekIndex < livedWeeks;
                const isCurrent = weekIndex === livedWeeks;

                const x = startX + col * (cubeSize + cubeGap);
                const y = startY + row * (cubeSize + cubeGap);

                drawMatrixCube(ctx, x, y, cubeSize, isLived, isCurrent);
                weekIndex++;
            }
        }

        ctx.restore();

        // Add branding footer
        ctx.fillStyle = '#6B7280';
        ctx.font = '24px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Made with Finite • finite.eleventh-photon.com', outputWidth / 2, outputHeight - 40);

        return new Promise((resolve, reject) => {
            outputCanvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Failed to generate full story image'));
            }, 'image/png', 1.0);
        });
    } finally {
        document.body.removeChild(clone);
    }
}

/**
 * Draw a single cube in the matrix (simple rounded square)
 */
function drawMatrixCube(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    isLived: boolean,
    isCurrent: boolean
): void {
    let fillColor: string;

    if (isCurrent) {
        fillColor = '#10B981'; // Emerald for current
    } else if (isLived) {
        fillColor = '#F59E0B'; // Amber for lived
    } else {
        fillColor = 'rgba(26, 26, 26, 0.08)'; // Light gray for remaining
    }

    ctx.fillStyle = fillColor;

    // Draw rounded rectangle
    const radius = size * 0.2;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + size - radius, y);
    ctx.quadraticCurveTo(x + size, y, x + size, y + radius);
    ctx.lineTo(x + size, y + size - radius);
    ctx.quadraticCurveTo(x + size, y + size, x + size - radius, y + size);
    ctx.lineTo(x + radius, y + size);
    ctx.quadraticCurveTo(x, y + size, x, y + size - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
}

/**
 * Generate a placeholder image for immediate preview
 */
function generatePlaceholderImage(format: string): Blob {
    const canvas = document.createElement('canvas');
    let width = 800;
    let height = 800;

    switch (format) {
        case 'square':
            width = 1080;
            height = 1080;
            break;
        case 'landscape':
            width = 1920;
            height = 1080;
            break;
        case 'story':
            width = 1080;
            height = 1920;
            break;
        case 'full':
            width = 800;
            height = 1500;
            break;
    }

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    // Background
    ctx.fillStyle = '#F5F3F0';
    ctx.fillRect(0, 0, width, height);

    // Padding
    const p = width * 0.05;

    // Header Skeleton
    ctx.fillStyle = '#E5E7EB'; // Lighter gray
    ctx.fillRect(p, p, width - p * 2, height * 0.1);

    // Stats Skeleton
    ctx.fillRect(p, p + height * 0.12, width - p * 2, height * 0.15);

    // Visualization Grid Skeleton
    ctx.fillStyle = '#D1D5DB'; // Slightly darker
    const vizY = p + height * 0.3;
    const vizHeight = height - vizY - p - (height * 0.1);
    ctx.fillRect(p, vizY, width - p * 2, vizHeight);

    // Loading Text
    ctx.fillStyle = '#9CA3AF';
    ctx.font = `bold ${width * 0.04}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Generating Preview...', width / 2, height / 2);

    // Convert to blob synchronously via data URI
    const dataUrl = canvas.toDataURL('image/png');
    const byteString = atob(dataUrl.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: 'image/png' });
}
